export type SegmentKind = 'scene' | 'connector';

export interface TimelineSegment {
  kind: SegmentKind;
  sceneIndex: number;
  start: number;
  end: number;
  units: number;
  asset?: string;
  mobileAsset?: string;
}

export interface TimelineScene {
  scrollUnits?: number;
  clip?: string;
  mobileClip?: string;
}

export interface TimelineOptions {
  sceneUnits: number;
  connectorUnits: number;
  connectors?: Array<string | undefined>;
  mobileConnectors?: Array<string | undefined>;
}

export function clamp(value: number, minimum = 0, maximum = 1): number {
  return Math.min(maximum, Math.max(minimum, value));
}

export function smoothstep(value: number): number {
  const x = clamp(value);
  return x * x * (3 - 2 * x);
}

export function lingerEase(value: number, linger: number): number {
  const x = clamp(value);
  const amount = clamp(linger);
  const centered = x - 0.5;
  return (1 - amount) * x + amount * (4 * centered * centered * centered + 0.5);
}

export function buildTimeline(
  scenes: TimelineScene[],
  options: TimelineOptions,
): TimelineSegment[] {
  const segments: TimelineSegment[] = [];
  let cursor = 0;

  scenes.forEach((scene, sceneIndex) => {
    const units = scene.scrollUnits ?? options.sceneUnits;
    segments.push({
      kind: 'scene',
      sceneIndex,
      start: cursor,
      end: cursor + units,
      units,
      asset: scene.clip,
      mobileAsset: scene.mobileClip,
    });
    cursor += units;

    const connector = options.connectors?.[sceneIndex];
    if (sceneIndex < scenes.length - 1 && connector) {
      segments.push({
        kind: 'connector',
        sceneIndex,
        start: cursor,
        end: cursor + options.connectorUnits,
        units: options.connectorUnits,
        asset: connector,
        mobileAsset: options.mobileConnectors?.[sceneIndex],
      });
      cursor += options.connectorUnits;
    }
  });

  return segments;
}

export function localProgress(segment: TimelineSegment, scrollUnits: number): number {
  return clamp((scrollUnits - segment.start) / (segment.end - segment.start));
}

export function journeyProgress(scrollUnits: number, totalUnits: number): number {
  if (totalUnits <= 0) return 0;
  return clamp(scrollUnits / totalUnits);
}

export function handoffProgress(
  scrollUnits: number,
  journeyEnd: number,
  handoffUnits: number,
): number {
  if (handoffUnits <= 0) return scrollUnits >= journeyEnd ? 1 : 0;
  return clamp((scrollUnits - journeyEnd) / handoffUnits);
}

export function activeSegmentIndex(segments: TimelineSegment[], scrollUnits: number): number {
  if (segments.length === 0) return -1;

  let active = 0;
  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];
    if (segment && scrollUnits >= segment.start) active = index;
  }
  return active;
}

export function nearestSceneIndex(
  segment: TimelineSegment,
  progress: number,
  sceneCount: number,
): number {
  const index =
    segment.kind === 'connector' && progress > 0.5 ? segment.sceneIndex + 1 : segment.sceneIndex;
  return clamp(index, 0, sceneCount - 1);
}

/**
 * Returns the five authored resting places for the magnetic journey. The first
 * and last scenes rest at the film boundaries; interior scenes rest at their
 * timeline centres so their copy is fully resolved when scrolling stops.
 */
export function sceneSnapAnchors(segments: TimelineSegment[], sceneCount: number): number[] {
  if (sceneCount <= 0) return [];

  return Array.from({ length: sceneCount }, (_, sceneIndex) => {
    const segment = segments.find(
      (candidate) => candidate.kind === 'scene' && candidate.sceneIndex === sceneIndex,
    );
    if (!segment) return 0;
    if (sceneIndex === 0) return segment.start;
    if (sceneIndex === sceneCount - 1) return segment.end;
    return segment.start + segment.units * 0.5;
  });
}

export function nearestSnapIndex(anchors: number[], scrollUnits: number): number {
  if (anchors.length === 0) return -1;

  return anchors.reduce((nearest, anchor, index) => {
    const nearestDistance = Math.abs((anchors[nearest] ?? anchor) - scrollUnits);
    return Math.abs(anchor - scrollUnits) < nearestDistance ? index : nearest;
  }, 0);
}

export function mediaSnapProgress(index: number, count: number): number {
  if (count <= 1) return 0;
  return clamp(index / (count - 1));
}
